import React, { useState } from 'react';
import { Card, CardHeader, CardContent, TextField, Grid, CardActions, Button } from '@material-ui/core';
import { Autocomplete } from 'formik-material-ui-lab';
import { Switch } from 'formik-material-ui';
import { searchUsers } from 'utils/SysUserUtils';
import { Formik, Field, Form } from 'formik';
import { addResource } from 'utils/ResourcesUtils';
import { useAppContext } from 'hooks/useAppContext';
import { NotificationsSnackBar } from 'components/Shared/DynamicForm';
import useResources from 'hooks/useResources';

/**
 * AddResource Component -
 * form for adding a resource assignment to a project
 * does not use DynamicForm Component because the form uses typeahead component that is not supported in DynamicForm.
 * @param {object} props 
 * @param { function } props.callback callback function that is called on a successful form submission;
 * @returns 
 */
const AddResource = ({ callback }) => {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [managerSearch, setManagerSearch] = useState('');
  const [managerOptions, setManagerOptions] = useState([]);
  const { selectedProject } = useAppContext();
  const { searchResources, findResourceByName } = useResources();
  const refreshLagTime = 1500;

  const handleChange = async (e) => {
    const val = e.target.value;
    setSearch(val);
    const data = await searchUsers(val);
    setOptions(data);
  }

  const handleManagerChange = async (e) => {
    const val = e.target.value;
    setManagerSearch(val);
    let data = searchResources(val);
    setManagerOptions(data);
  };

  const onSubmit = async (values, actions) => {
    if (options.find(r => r.Name === values.resourceName)) {
      const payload = {
        ProjectId: selectedProject,
        SystemUserId: options.find(r => r.Name === values.resourceName).SystemUserId,
        QcReqYn: values.requireQC,
        ...values.managerName !== '' && { ManagerAssignmentId: findResourceByName(values.managerName) }
      }
      const result = await addResource(payload);
      if (result.success) {
        actions.setSubmitting(false);
        actions.resetForm();
        setSearch('');
        await new Promise(resolve => setTimeout(resolve, refreshLagTime));
        actions.setStatus({ success: true, message: "Form was submitted successfully", alertType: "success" });
        callback();
      } else {
        await new Promise(resolve => setTimeout(resolve, refreshLagTime));
        console.log(result);
        let message = "There was an issue submitting the form"
        try {
          message = `${message}. ${result.details.message}`;
        } catch(e){}
        actions.setStatus({ success: false, message: message, alertType: "error" });
      }
    }
  }

  return (
    <Grid container justify="center" style={{ padding: 16 }}>
      <Grid item xs={12} sm={12} md={12} lg={7} xl={7}>
        <Card>
          <Formik
            initialValues={{
              resourceName: '',
              requireQC: false,
              managerName: ''
            }}
            onSubmit={onSubmit}
          >
            {props => (
              <Form>
                <CardHeader title="Add a Resource"></CardHeader>
                <CardContent>
                  <Grid item xs={12} sm={12} md={12} lg={7} xl={7}>
                    <Field
                      name="resourceName"
                      component={Autocomplete}
                      options={options}
                      getOptionLabel={(option) => option.Name || ''}
                      onChange={(e) => { props.setFieldValue("resourceName", e.target.textContent) }}
                      inputValue={props.values.resourceName.length > 0 ? props.values.resourceName : search}
                      onInputChange={(e, val, reason) => reason === "clear" && props.setFieldValue('resourceName', '') && setSearch('')}
                      renderInput={
                        (params) => (
                          <TextField
                            {...params}
                            label="Search for a resource"
                            variant="outlined"
                            onChange={handleChange} error={props.touched.resourceName && !options.find(r => r.Name === props.values.resourceName)}
                          />
                        )
                      }
                    />
                  </Grid>
                  {props.values.resourceName &&
                    <>
                      <Grid item xs={7} style={{ marginTop: 16 }}>
                        <Field component={Switch} name="requireQC" type="checkbox" />Require QC?
                    </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={7} xl={7} style={{ marginTop: 16 }}>
                        <Field
                          name='managerName'
                          component={Autocomplete}
                          options={managerOptions}
                          getOptionLabel={(option) => option.Name_SystemUser || ''}
                          onChange={(e) => { props.setFieldValue('managerName', e.target.textContent) }}
                          inputValue={props.values.managerName.length > 0 ? props.values.managerName : managerSearch}
                          onInputChange={(e, val, reason) => reason === "clear" && props.setFieldValue('managerName', '') && setManagerSearch('')}
                          renderInput={
                            (params) => (
                              <TextField
                                {...params}
                                label="Search for a manager"
                                variant="outlined"
                                onChange={handleManagerChange} error={props.touched.manager && !managerOptions.find(r => r.Name === props.values.manager)}
                              />
                            )
                          }
                        />
                      </Grid>
                    </>
                  }
                </CardContent>
                <CardActions style={{ padding: 16 }}>
                  <Button variant="contained" color="primary" type="submit">Add</Button>
                </CardActions>
                <NotificationsSnackBar status={props.status} setStatus={props.setStatus} refreshLagTime={3000}></NotificationsSnackBar>
              </Form>
            )}
          </Formik>
        </Card>
      </Grid>
    </Grid >
  )
}

export default AddResource;